import React, { useEffect, useState } from "react";

import { useStreamingAdmin } from "../../../hooks/useStreamingAdmin";
import { getStreamTypeConfig } from "../../../services/API/StreamingAdminApi";
import { columnsToFields, fieldsToColumns, validateColumns } from "../../../utils/streamColumns";
import MiniModal from "../Modal/MiniModal";
import { STATUS_ACTIONS_FORM } from "./types/StatusActions";

const StreamingTypeConfigForm = ({
  dashboards,
  onSave,
  onCancel,
  action = "modify",
}) => {
  const {
    setProductToStreamType,
    productToStreamType,
    refetchTypeOfStream,
    typesOfStreams,
    triggerModifyStreamType,
    triggerCreateStreamType,
  } = useStreamingAdmin();

  const [selectedDashboard, setSelectedDashboard] = useState("");
  const [streamingType, setStreamingType] = useState("");
  const [fields, setFields] = useState([]);
  const [isMiniModalVisible, setIsMiniModalVisible] = useState(false);
  // where the fields came from: current configuration, a JSON file, or manual
  const [fieldsInfo, setFieldsInfo] = useState("");


  // Llama a refetchTypeOfStream cuando el dashboard cambia
  useEffect(() => {
    if (productToStreamType && action === "modify") {
      refetchTypeOfStream();
    }
  }, [productToStreamType]);

  useEffect(() => {
    if (selectedDashboard && streamingType && fields.length === 0) {
      setFields([{ key: "", type: "float" }]);
    }
  }, [selectedDashboard, streamingType]);

  // Modify: start from the stream type's current configuration (saving replaces the
  // whole configuration, so the fields must contain every column)
  useEffect(() => {
    if (action !== STATUS_ACTIONS_FORM.MODIFY || !selectedDashboard || !streamingType) return;
    let cancelled = false;
    setFieldsInfo("Loading the current configuration...");
    getStreamTypeConfig(selectedDashboard, streamingType)
      .then((response) => {
        if (cancelled) return;
        if (response?.statusType === "SUCCESS") {
          const { columns, updated_at } = response.data;
          setFields(columnsToFields(columns));
          setFieldsInfo(`Current configuration loaded: ${Object.keys(columns).length} columns` +
            (updated_at ? ` (last updated ${new Date(updated_at).toLocaleString()})` : "") + ".");
        } else {
          setFieldsInfo(response?.statusMessage || "Could not load the current configuration.");
        }
      })
      .catch(() => !cancelled && setFieldsInfo("Could not load the current configuration."));
    return () => { cancelled = true; };
  }, [action, selectedDashboard, streamingType]);

  // Fill the fields from a JSON file like {"column_name": "float", "other": "str"}
  const handleJsonFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow choosing the same file again
    if (!file) return;
    let columns;
    try {
      columns = JSON.parse(await file.text());
    } catch {
      alert(`"${file.name}" is not a valid JSON file.`);
      return;
    }
    const error = validateColumns(columns);
    if (error) {
      alert(`"${file.name}": ${error}`);
      return;
    }
    setFields(columnsToFields(columns));
    setFieldsInfo(`Loaded ${Object.keys(columns).length} columns from "${file.name}". Review them and click Save.`);
  };

  // Maneja el cambio de dashboard
  const handleDashboardChange = (e) => {
    const dashboard = e.target.value;
    setSelectedDashboard(dashboard);
    setProductToStreamType(dashboard);
    setStreamingType("");
    setFields([]);
    setFieldsInfo("");
  };

  // Maneja la selección o escritura del streaming type
  const handleStreamingTypeChange = (e) => {
    setStreamingType(e.target.value);
    setFields([]);
    setFieldsInfo("");
  };

  // Agrega un nuevo campo dinámico
  const addField = () => {
    setFields((prevFields) => [...prevFields, { key: "", type: "float" }]);
  };

  // Elimina un campo dinámico por índice
  const removeField = (index) => {
    setFields((prevFields) => prevFields.filter((_, i) => i !== index));
  };

  const confirmSave = async () => {
    const formattedFields = fieldsToColumns(fields);

    try {
      let response;
      if (action === STATUS_ACTIONS_FORM.CREATE) {
        response = await triggerCreateStreamType(
          selectedDashboard,
          streamingType,
          formattedFields
        );
      } else if (action === STATUS_ACTIONS_FORM.MODIFY) {
        response = await triggerModifyStreamType(
          selectedDashboard,
          streamingType,
          formattedFields
        );
      }
      setIsMiniModalVisible(false);
      if (response?.statusType !== "SUCCESS") {
        alert(`Error: ${response?.statusMessage || "The stream type could not be saved."}`);
        return;
      }
      onSave(formattedFields);
    } catch (error) {
      console.error("Error al procesar el stream type:", error.message);
      alert(`Error: ${error.message}`);
    }
  };


  const handleSave = () => {
    if (!selectedDashboard || !streamingType.trim()) {
      alert("Please select a dashboard and provide a streaming type name.");
      return;
    }
    if (fields.length === 0) {
      alert("Please add at least one field.");
      return;
    }
    const error = validateColumns(fieldsToColumns(fields));
    if (error) {
      alert(error);
      return;
    }
    setIsMiniModalVisible(true);
  };

  return (
    <div className="flex flex-col space-y-4 w-full max-w-xl">
      {/* Mini Modal */}
      {isMiniModalVisible && (
        <MiniModal
          title="Confirm Changes"
          message={`Are you sure you want to ${action === "create" ? "create" : "modify"
            } this streaming type?`}
          actions={[
            {
              label: "Cancel",
              onClick: () => setIsMiniModalVisible(false),
              className: "danger",
            },
            {
              label: "Confirm",
              onClick: confirmSave,
              className: "primary",
            },
          ]}
        />
      )}

      {/* Select de Dashboard */}
      <div>
        <label htmlFor="dashboard" className="block text-sm font-bold mb-2">
          Select Dashboard
        </label>
        <select
          id="dashboard"
          value={selectedDashboard}
          onChange={handleDashboardChange}
          className="w-full p-2 bg-gray-700 rounded-lg text-white"
        >
          <option value="">-- Select a Dashboard --</option>
          {dashboards.map((dashboard) => (
            <option key={dashboard.value} value={dashboard.value}>
              {dashboard.label}
            </option>
          ))}
        </select>
      </div>

      {/* Streaming Type Input o Select */}
      {selectedDashboard && (
        <div>
          <label htmlFor="streamingType" className="block text-sm font-bold mb-2">
            {action === "create" ? "Streaming Type Name" : "Select Streaming Type"}
          </label>
          {action === "create" ? (
            <input
              id="streamingType"
              type="text"
              placeholder="Enter new streaming type name"
              value={streamingType}
              onChange={(e) => setStreamingType(e.target.value)}
              className="w-full p-2 bg-gray-700 rounded-lg text-white"
            />
          ) : (
            <select
              id="streamingType"
              value={streamingType}
              onChange={handleStreamingTypeChange}
              className="w-full p-2 bg-gray-700 rounded-lg text-white"
            >
              <option value="">-- Select a Streaming Type --</option>
              {typesOfStreams.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      {/* Campos dinámicos */}
      {selectedDashboard && streamingType && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold">Fields Configuration</h3>
            <label className="bg-gray-700 hover:bg-gray-600 font-bold py-2 px-4 rounded-lg transition duration-300 cursor-pointer text-sm">
              Load from JSON file
              <input type="file" accept=".json,application/json" onChange={handleJsonFile} className="hidden" />
            </label>
          </div>
          {fieldsInfo && <p className="text-sm text-gray-300">{fieldsInfo}</p>}
          {action === STATUS_ACTIONS_FORM.MODIFY && (
            <p className="text-xs text-gray-400">
              Saving replaces the whole configuration with these fields. Running streams use it after their next reset.
            </p>
          )}
          {fields.map((field, index) => (
            <div key={index} className="flex items-center space-x-2">
              <input
                type="text"
                placeholder="Field Name"
                value={field.key}
                onChange={(e) =>
                  setFields((prevFields) =>
                    prevFields.map((f, i) =>
                      i === index ? { ...f, key: e.target.value } : f
                    )
                  )
                }
                className="flex-1 p-2 bg-gray-700 rounded-lg text-white"
              />
              <select
                value={field.type}
                onChange={(e) =>
                  setFields((prevFields) =>
                    prevFields.map((f, i) =>
                      i === index ? { ...f, type: e.target.value } : f
                    )
                  )
                }
                className="p-2 bg-gray-700 rounded-lg text-white"
              >
                <option value="float">float</option>
                <option value="str">str</option>
              </select>
              {fields.length > 1 && (
                <button
                  onClick={() => removeField(index)}
                  className="bg-red-600 hover:bg-red-500 font-bold py-2 px-3 rounded-lg transition duration-300"
                >
                  -
                </button>
              )}
              {index === fields.length - 1 && (
                <button
                  onClick={addField}
                  className="bg-green-600 hover:bg-green-500 font-bold py-2 px-3 rounded-lg transition duration-300"
                >
                  +
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Botones de acción (always visible at the bottom of the scrolling modal) */}
      <div className="flex justify-end space-x-4 sticky bottom-0 bg-gray-800 pt-3 pb-1 -mb-1">
        <button
          onClick={onCancel}
          className="bg-gray-700 hover:bg-gray-600 font-bold py-2 px-4 rounded-lg transition duration-300"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          className="bg-blue-600 hover:bg-blue-500 font-bold py-2 px-4 rounded-lg transition duration-300"
        >
          Save
        </button>
      </div>
    </div>
  );
};

export default StreamingTypeConfigForm;
